/**
 * Thin wrappers around zafroi-mvp-runtime's /api/v1/auth/* endpoints.
 *
 * Uses axios directly (not the apiClient with its refresh interceptor) so
 * /login / /refresh themselves can't trigger a refresh cycle on failure.
 *
 * `withCredentials: true` is set on every call so the browser sends the
 * cross-subdomain refresh-token cookie set by /auth/login (Phase 2 SSO).
 * Without it the cookie is silently dropped on cross-origin XHR.
 */

import axios from 'axios';
import type {
  CurrentUser,
  LoginPayload,
  LoginResult,
  MfaVerifyPayload,
  RefreshResponse,
} from '@/types/auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';
const AUTH_BASE = `${BASE_URL.replace(/\/$/, '')}/api/v1/auth`;
const WITH_CREDS = { withCredentials: true };

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string; details?: unknown };
}

async function envelope<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  const body = res.data;
  if (body?.success === false || !body?.data) {
    const msg = body?.error?.message ?? 'Request failed';
    const err = new Error(msg) as Error & {
      code?: string;
      details?: unknown;
    };
    err.code = body?.error?.code;
    err.details = body?.error?.details;
    throw err;
  }
  return body.data;
}

export const authApi = {
  login: (payload: LoginPayload) =>
    envelope<LoginResult>(axios.post(`${AUTH_BASE}/login`, payload, WITH_CREDS)),

  mfaVerify: (payload: MfaVerifyPayload) =>
    envelope<LoginResult>(axios.post(`${AUTH_BASE}/mfa/verify`, payload, WITH_CREDS)),

  // Phase 2 SSO: refreshToken is optional. When undefined, the backend
  // reads it from the HttpOnly cookie set by /auth/login. That's how the
  // platform → Kosh SSO works: a user logged in at zafroi.com lands on
  // kosh.zafroi.com with no localStorage but with a valid cookie.
  refresh: (refreshToken?: string) =>
    envelope<RefreshResponse>(
      axios.post(
        `${AUTH_BASE}/refresh`,
        refreshToken ? { refreshToken } : {},
        WITH_CREDS,
      ),
    ),

  me: (accessToken: string) =>
    envelope<CurrentUser>(
      axios.get(`${AUTH_BASE}/me`, {
        ...WITH_CREDS,
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
    ),

  logout: (accessToken: string) =>
    envelope<{ ok: true }>(
      axios.post(
        `${AUTH_BASE}/logout`,
        {},
        {
          ...WITH_CREDS,
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      ),
    ),
};
